// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface MenuOption {
	label: string;
	action: string;
	hasIndicator?: boolean;
}

declare global {
	interface HTMLElementEventMap {
		'menu-action': CustomEvent<{ action: string }>;
	}
}

@customElement('runtime-menu')
export class RuntimeMenu extends LitElement {
	@property({ type: Array })
	menuOptions: MenuOption[] = [
		{ label: 'Device Registration', action: 'device-registration', hasIndicator: true },
	];

	@state()
	isVisible = false;

	@state()
	private selectedIndex = 0;

	static override styles = css`\
:host {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: linear-gradient(135deg, #0f1419 0%, #1a2332 50%, #2d3748 100%);
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	padding: 0;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
	font-size: 12px;
	color: #ffffff;
	opacity: 0;
	visibility: hidden;
	transition: all 0.3s ease-in-out;
	z-index: 1000;
}

:host([visible]) {
	opacity: 1;
	visibility: visible;
}

.menu-container {
	position: relative;
	width: 100%;
	padding: 60px 0 0 100px;
	display: flex;
	justify-content: flex-start;
	z-index: 10;
}

.menu-wrapper {
	position: relative;
}

.nav-hint {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	width: 32px;
	height: 32px;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	font-weight: bold;
	color: rgba(255, 255, 255, 0.7);
}

.nav-hint.left {
	left: -60px;
}

.nav-hint.right {
	right: -60px;
}

.menu-list {
	display: flex;
	list-style: none;
	font-size: 14px;
	margin: 0;
	padding: 0;
	gap: 60px;
	align-items: center;
}

.menu-item {
	position: relative;
	display: flex;
	align-items: center;
	cursor: pointer;
	transition: all 0.2s ease;
	white-space: nowrap;
}

.menu-item-content {
	position: relative;
	display: flex;
	align-items: center;
	gap: 8px;
}

.menu-item-text {
	color: rgba(255, 255, 255, 0.7);
	transition: color 0.2s ease;
	position: relative;
	padding-bottom: 4px;
}

.menu-item.selected .menu-item-text {
	color: #ffffff;
}

.menu-item.selected .menu-item-text::after {
	content: '';
	position: absolute;
	bottom: -2px;
	left: 0;
	right: 0;
	height: 2px;
	background: #ffffff;
}

.indicator {
	position: absolute;
	top: -8px;
	right: -12px;
	width: 8px;
	height: 8px;
	background: #ff4444;
	border-radius: 50%;
	box-shadow: 0 0 8px rgba(255, 68, 68, 0.6);
	animation: glow 1s ease-in-out infinite alternate;
}

@keyframes glow {
	0% {
		background: #ff4444;
		box-shadow: 0 0 8px rgba(255, 68, 68, 0.6);
	}
	100% {
		background: #ffd7ca;
		box-shadow: 0 0 5px 2px rgba(255, 102, 0, 0.8), 0 0 30px rgba(255, 102, 0, 0.4);
	}
}

.action-container {
	width: 100%;
	padding: 40px;
}

.background-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background:
		radial-gradient(circle at 20% 80%, rgba(124, 58, 237, 0.1) 0%, transparent 50%),
		radial-gradient(circle at 80% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
	pointer-events: none;
}`;

	private _onKeyDown = this.onKeyDown.bind(this);
	private _isKeyDownListening = false;

	private addKeyDownListener(): void {
		if(!this._isKeyDownListening) {
			this._isKeyDownListening = true;
			document.addEventListener('keydown', this._onKeyDown);
		}
	}

	private removeKeyDownListener(): void {
		if(this._isKeyDownListening) {
			this._isKeyDownListening = false;
			document.removeEventListener('keydown', this._onKeyDown);
		}
	}

	override connectedCallback(): void {
		super.connectedCallback();
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.removeKeyDownListener();
	}

	private resetMenu(): void {
		this.selectedIndex = 0;
	}

	private onKeyDown(event: KeyboardEvent): void {
		if(!this.isVisible) return;

		switch(event.key) {
		case 'q':
			event.preventDefault();
			this.selectedIndex = this.selectedIndex > 0
				? this.selectedIndex - 1
				: this.menuOptions.length - 1;
			this.requestUpdate('selectedIndex');
			break;

		case 'e':
			event.preventDefault();
			this.selectedIndex = this.selectedIndex < this.menuOptions.length - 1
				? this.selectedIndex + 1
				: 0;
			this.requestUpdate('selectedIndex');
			break;

		case 'Escape':
			event.preventDefault();
			this.hideMenu();
			break;

		default: break;
		}
	}

	showMenu(): void {
		this.isVisible = true;
		this.resetMenu();
		this.addKeyDownListener();
	}

	hideMenu(): void {
		this.isVisible = false;
		this.removeKeyDownListener();
		this.dispatchEvent(new CustomEvent('menu-hide'));
	}

	private async executeSelectedOption(): Promise<void> {
		const selectedOption = this.menuOptions[this.selectedIndex];
		if(!selectedOption) return;

		if(selectedOption.action) {
			// Dispatch custom event for menu action
			this.dispatchEvent(new CustomEvent('menu-action', {
				detail: { action: selectedOption.action },
				bubbles: true,
				composed: true
			}));
		}
	}

	override updated(changedProperties: PropertyValues) {
		super.updated(changedProperties);
		if(changedProperties.has('isVisible')) {
			this.toggleAttribute('visible', this.isVisible);
		}
		if(changedProperties.has('selectedIndex')) {
			const selectedOption = this.menuOptions[this.selectedIndex];
			if(selectedOption) {
				const actionEvent = new CustomEvent('menu-action', {
					detail: { action: selectedOption.action || null },
					bubbles: true,
					composed: true
				});
				this.dispatchEvent(actionEvent);
			}
		}
	}

	override render() {
		return html`
<div class="container">
	<div class="background-overlay"></div>
	<div class="menu-container">
		<div class="menu-wrapper">
			<div class="nav-hint left">Q</div>
			<ul class="menu-list">
				${this.menuOptions.map((option, index) => html`
					<li
						class="menu-item ${index === this.selectedIndex ? 'selected' : ''}"
						@click=${() => { this.selectedIndex = index; this.executeSelectedOption(); }}
					>
						<div class="menu-item-content">
							<span class="menu-item-text">${option.label}</span>
							${option.hasIndicator ? html`<div class="indicator"></div>` : ''}
						</div>
					</li>
				`)}
			</ul>
			<div class="nav-hint right">E</div>
		</div>
	</div>
	<div class="action-container"><slot></slot></div>
</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'runtime-menu': RuntimeMenu;
	}
}
